// Reportes tributarios SRI (Ecuador): Formulario 104 (IVA), Formulario 103
// (retenciones IR) y Conciliación tributaria (F101). Contabilidad → Reportes.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { exportarCSV, field, usd, rpc } from './shared';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const HOY = new Date();
const CsvBtn = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} className="no-print inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted"><Download className="w-4 h-4" /> CSV</button>
);
const Row = ({ l, v, bold }: { l: string; v: string; bold?: boolean }) => (
  <div className={`flex justify-between py-1.5 border-b last:border-0 ${bold ? 'font-semibold border-t-2' : ''}`}><span>{l}</span><span className="font-mono tabular-nums">{v}</span></div>
);

// El F104 dice cuánto se declara; esto lo lleva a los libros. Sin este paso,
// «IVA en ventas» e «IVA en compras» crecen mes a mes en el balance como si
// nada se hubiera declarado — y al cabo de un año son dos cuentas de miles que
// no corresponden a ninguna obligación real con el SRI.
function LiquidacionIva({ anio, mes }: { anio: number; mes: number }) {
  const qc = useQueryClient();
  const sim = useQuery<any>({
    queryKey: ['ivaLiq', anio, mes],
    queryFn: () => rpc<any>('cont_iva_liquidar', { _anio: anio, _mes: mes, _confirmar: false }),
  });
  const liquidar = useMutation({
    mutationFn: () => rpc<any>('cont_iva_liquidar', { _anio: anio, _mes: mes, _confirmar: true }),
    onSuccess: (r: any) => {
      toast.success(r?.mensaje || 'IVA liquidado');
      void qc.invalidateQueries({ queryKey: ['ivaLiq'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const d = sim.data;
  const saldo = Number(d?.saldo ?? 0);
  const nada = Number(d?.iva_ventas ?? 0) === 0 && Number(d?.iva_compras ?? 0) === 0
            && Number(d?.retenciones ?? 0) === 0;

  return (
    <Card className="no-print mt-4">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base">Liquidación contable del IVA</CardTitle>
        {d?.ya_liquidado && (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
            ya liquidado
          </span>)}
      </CardHeader>
      <CardContent className="max-w-2xl space-y-3">
        {sim.isFetching ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary my-4" /> : (
          <>
            <p className="text-sm">{d?.mensaje}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
              <div className="rounded-md border p-2"><span className="block text-[11px] text-muted-foreground">IVA cobrado</span>
                <span className="font-mono tabular-nums">{usd(d?.iva_ventas)}</span></div>
              <div className="rounded-md border p-2"><span className="block text-[11px] text-muted-foreground">Crédito del mes</span>
                <span className="font-mono tabular-nums">{usd(d?.iva_compras)}</span></div>
              <div className="rounded-md border p-2"><span className="block text-[11px] text-muted-foreground">Arrastre anterior</span>
                <span className="font-mono tabular-nums">{usd(d?.credito_anterior)}</span></div>
              <div className="rounded-md border p-2"><span className="block text-[11px] text-muted-foreground">{saldo >= 0 ? 'A pagar al SRI' : 'A favor'}</span>
                <span className={`font-mono tabular-nums font-semibold ${saldo > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{usd(Math.abs(saldo))}</span></div>
            </div>
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              El asiento sale de <b>este mismo formulario</b>, no de otro cálculo: cancela el IVA del mes
              contra el crédito y las retenciones, y deja en la cuenta sólo el saldo real frente al SRI.
              Volver a liquidar el mismo mes <b>reemplaza</b> el asiento, no lo duplica.
            </p>
            <div className="flex justify-end">
              <button
                disabled={nada || liquidar.isPending}
                onClick={() => liquidar.mutate()}
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50">
                {d?.ya_liquidado ? 'Recalcular la liquidación' : 'Generar el asiento'}
              </button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// `tabInicial` permite que la Conciliación Tributaria (F101) tenga su propia
// opción de menú: es un entregable ANUAL y estaba escondido como tercera
// pestaña entre dos formularios mensuales.
export function Tributario({ tabInicial = '104' }: { tabInicial?: '104' | '103' | '101' } = {}) {
  const [tab, setTab] = useState<'104' | '103' | '101'>(tabInicial);
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [noDed, setNoDed] = useState(0);
  const [tarifa, setTarifa] = useState(25);
  const [reten, setReten] = useState(0);

  const f104 = useQuery<any>({ queryKey: ['trib104', anio, mes], enabled: tab === '104', queryFn: () => rpc<any>('trib_form_104', { _anio: anio, _mes: mes }) });
  const f103 = useQuery<any>({ queryKey: ['trib103', anio, mes], enabled: tab === '103', queryFn: () => rpc<any>('trib_form_103', { _anio: anio, _mes: mes }) });
  const f101 = useQuery<any>({ queryKey: ['trib101', anio, noDed, tarifa, reten], enabled: tab === '101', queryFn: () => rpc<any>('trib_conciliacion', { _anio: anio, _no_deducibles: noDed, _tarifa: tarifa, _retenciones: reten }) });
  // Los gastos no deducibles que el sistema puede PROBAR — hoy, el exceso de
  // depreciación contable sobre la tarifa máxima LORTI. Copiar ese número a
  // mano de una pantalla a otra del MISMO sistema es lo que un ERP existe para
  // eliminar; pero se OFRECE, no se impone: la casilla admite más conceptos
  // (multas, gastos sin sustento) que sólo conoce el contador.
  const auto = useQuery<{ total: number; origenes: { origen: string; importe: number; detalle: string }[] }>({
    queryKey: ['trib-no-ded', anio], enabled: tab === '101',
    queryFn: () => rpc('trib_no_deducibles', { _anio: anio }),
  });

  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="cont-tributario" antes="Tributario (SRI)" />
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">{tabInicial === '101' ? 'Conciliación Tributaria (F101)' : 'Formularios SRI (F104 · F103)'}</h1><PrintButton /></div>
      <p className="text-sm text-muted-foreground no-print">Insumos declarativos referenciales. Verifica los valores contra la normativa y los formularios oficiales del período antes de declarar.</p>
      <div className="flex gap-1 rounded-lg border p-1 w-fit no-print">
        {([['104', 'Form. 104 (IVA)'], ['103', 'Form. 103 (Ret. fuente)'], ['101', 'Conciliación (F101)']] as const).map(([k, t]) => (
          <button key={k} onClick={() => setTab(k)} className={`rounded-md px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
        ))}
      </div>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Año<input type="number" className={`${field} w-28 mt-1`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} /></label>
        {tab !== '101' && <label className="flex flex-col text-xs text-muted-foreground">Mes<select className={`${field} w-40 mt-1`} value={mes} onChange={(e) => setMes(Number(e.target.value))}>{MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></label>}
        {tab === '101' && <>
          <label className="flex flex-col text-xs text-muted-foreground">Gastos no deducibles<input type="number" step="0.01" className={`${field} w-36 mt-1`} value={noDed} onChange={(e) => setNoDed(Number(e.target.value))} /></label>
          {(auto.data?.total ?? 0) > 0 && (
            <button type="button"
              onClick={() => setNoDed(Number(auto.data?.total ?? 0))}
              title={(auto.data?.origenes ?? []).map((o) => `${o.origen}: ${o.importe}\n${o.detalle}`).join('\n\n')}
              className="mb-0.5 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs text-primary hover:bg-primary/10">
              El sistema calculó <b>{usd(auto.data?.total)}</b> — usar
            </button>
          )}
          <label className="flex flex-col text-xs text-muted-foreground">Tarifa IR %<input type="number" step="0.01" className={`${field} w-28 mt-1`} value={tarifa} onChange={(e) => setTarifa(Number(e.target.value))} /></label>
          <label className="flex flex-col text-xs text-muted-foreground">Retenciones/anticipo<input type="number" step="0.01" className={`${field} w-36 mt-1`} value={reten} onChange={(e) => setReten(Number(e.target.value))} /></label>
        </>}
      </CardContent></Card>

      {/* Formulario 104 — IVA */}
      {tab === '104' && (
        <Imprimible titulo="Formulario 104 — IVA" subtitulo={`${MESES[mes - 1]} ${anio}`}>
          <Card><CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-base">Resumen IVA</CardTitle>
            <CsvBtn onClick={() => { const d = f104.data || {}; exportarCSV(`form104-${anio}-${mes}`, ['Concepto', 'Valor'], [['Ventas gravadas', d.ventas_gravadas], ['Ventas exentas', d.ventas_exentas], ['IVA en ventas', d.iva_ventas], ['Compras (base)', d.compras_base], ['IVA en compras (crédito)', d.iva_compras], ['Retención IVA efectuada', d.retencion_iva_efectuada], ['IVA resultante', d.iva_resultante], ['Pagos por reembolsos (intermediario, informativo)', d.reembolso_pagos_intermediario], ['Reembolsos facturados (intermediario, informativo)', d.reembolso_facturado_intermediario]]); }} /></CardHeader>
            <CardContent className="max-w-xl">
              {f104.isFetching ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary my-6" /> : <>
                <Row l="Ventas gravadas (base)" v={usd(f104.data?.ventas_gravadas)} />
                <Row l="Ventas exentas / 0%" v={usd(f104.data?.ventas_exentas)} />
                <Row l="IVA en ventas (generado)" v={usd(f104.data?.iva_ventas)} />
                <Row l="Compras (base)" v={usd(f104.data?.compras_base)} />
                <Row l="IVA en compras (crédito tributario)" v={usd(f104.data?.iva_compras)} />
                <Row l="Retención de IVA efectuada" v={usd(f104.data?.retencion_iva_efectuada)} />
                <Row l={Number(f104.data?.iva_resultante || 0) >= 0 ? 'IVA a pagar' : 'Crédito tributario IVA'} v={usd(Math.abs(Number(f104.data?.iva_resultante || 0)))} bold />
                {(Number(f104.data?.reembolso_pagos_intermediario || 0) > 0 || Number(f104.data?.reembolso_facturado_intermediario || 0) > 0) && (
                  <div className="mt-3 rounded border border-dashed p-2">
                    <p className="mb-1 text-xs font-medium text-muted-foreground">Reembolsos como intermediario (informativo — no son compras ni ventas propias)</p>
                    <Row l="Pagos por reembolsos (compras adjuntas a facturas de reembolso)" v={usd(f104.data?.reembolso_pagos_intermediario)} />
                    <Row l="Reembolsos facturados al cliente (cód. 41)" v={usd(f104.data?.reembolso_facturado_intermediario)} />
                    <p className="mt-1 text-[11px] text-muted-foreground">Su IVA no entra en el crédito tributario de arriba: lo usa el cliente (RLRTI art. 36). Trasládelos a los casilleros informativos de reembolso del formulario.</p>
                  </div>
                )}
              </>}
            </CardContent></Card>
          <LiquidacionIva anio={anio} mes={mes} />
        </Imprimible>
      )}

      {/* Formulario 103 — Retenciones en la fuente */}
      {tab === '103' && (
        <Imprimible titulo="Formulario 103 — Retenciones en la fuente (IR)" subtitulo={`${MESES[mes - 1]} ${anio}`}>
          <Card><CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-base">Retenciones por código</CardTitle>
            <CsvBtn onClick={() => exportarCSV(`form103-${anio}-${mes}`, ['Código', 'Descripción', 'Base', 'Retenido'], (f103.data?.lineas || []).map((r: any) => [r.codigo, r.descripcion, r.base, r.retenido]))} /></CardHeader>
            <CardContent className="overflow-x-auto">
              {f103.isFetching ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary my-6" /> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3 text-right">Base imponible</th><th className="py-2 pr-3 text-right">Retenido</th></tr></thead>
                  <tbody>
                    {(f103.data?.lineas || []).length === 0 && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Sin retenciones en el período</td></tr>}
                    {(f103.data?.lineas || []).map((r: any, i: number) => (
                      <tr key={i} className="border-b last:border-0"><td className="py-1.5 pr-3 font-mono">{r.codigo}</td><td className="py-1.5 pr-3">{r.descripcion || '—'}</td><td className="py-1.5 pr-3 text-right">{usd(r.base)}</td><td className="py-1.5 pr-3 text-right">{usd(r.retenido)}</td></tr>
                    ))}
                    {(f103.data?.lineas || []).length > 0 && <tr className="border-t-2 font-bold"><td colSpan={2} className="py-2 pr-3">Totales</td><td className="py-2 pr-3 text-right">{usd(f103.data?.total_base)}</td><td className="py-2 pr-3 text-right">{usd(f103.data?.total_retenido)}</td></tr>}
                  </tbody>
                </table>
              )}
            </CardContent></Card>
        </Imprimible>
      )}

      {/* Conciliación tributaria F101 */}
      {tab === '101' && (
        <Imprimible titulo="Conciliación tributaria (F101)" subtitulo={`Ejercicio ${anio}`}>
          <Card><CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-base">Conciliación tributaria</CardTitle>
            <CsvBtn onClick={() => { const d = f101.data || {}; exportarCSV(`conciliacion-${anio}`, ['Concepto', 'Valor'], [['Utilidad contable', d.utilidad_contable], ['(+) Gastos no deducibles', d.gastos_no_deducibles], ['(-) 15% participación trabajadores', d.participacion_trabajadores], ['Base imponible', d.base_imponible], ['Tarifa IR %', d.tarifa], ['IR causado', d.ir_causado], ['(-) Retenciones/anticipo', d.retenciones_anticipo], ['Impuesto a pagar', d.impuesto_a_pagar]]); }} /></CardHeader>
            <CardContent className="max-w-xl">
              {f101.isFetching ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary my-6" /> : <>
                <Row l="Utilidad (pérdida) contable del ejercicio" v={usd(f101.data?.utilidad_contable)} />
                <Row l="(+) Gastos no deducibles" v={usd(f101.data?.gastos_no_deducibles)} />
                <Row l="(−) 15% Participación trabajadores" v={usd(f101.data?.participacion_trabajadores)} />
                <Row l="Base imponible" v={usd(f101.data?.base_imponible)} bold />
                <Row l={`IR causado (${f101.data?.tarifa ?? tarifa}%)`} v={usd(f101.data?.ir_causado)} />
                <Row l="(−) Retenciones / anticipo" v={usd(f101.data?.retenciones_anticipo)} />
                <Row l={Number(f101.data?.impuesto_a_pagar || 0) >= 0 ? 'Impuesto a pagar' : 'Saldo a favor'} v={usd(Math.abs(Number(f101.data?.impuesto_a_pagar || 0)))} bold />
              </>}
            </CardContent></Card>
        </Imprimible>
      )}
    </div>
  );
}
